import * as stylex from '@stylexjs/stylex';
import type { ReactElement } from 'react';
import { useSyncExternalStore } from 'react';

import { SESSION_STORAGE } from '@/constants/storage';
import {
  Cell,
  ChevronDownStrokeIcon,
  ChevronRightStrokeIcon,
  Column,
  EmptyState,
  Heading,
  Row,
  Table,
  TableBody,
  TableHeader,
  Text,
  View,
} from '@/libs/serenity-ui';
import { type PayoutContentData, type PayoutGroup, type PayoutLeaf, type PayoutTreeNode } from '@/resources/report/report.api';

const EXPANDED_KEY = SESSION_STORAGE.payoutExpandedIds.key;
const GROUP_IDS_KEY = SESSION_STORAGE.payoutGroupIds.key;
const CHANGE_EVENT = 'payout-expand-change';

function dispatch (): void {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe (callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  return () => window.removeEventListener(CHANGE_EVENT, callback);
}

// Cache the last snapshot to give useSyncExternalStore a stable reference when
// nothing has changed, avoiding unnecessary re-renders.
let cachedRaw = '';
let cachedSnapshot: ReadonlySet<string> = new Set();

function getSnapshot (): ReadonlySet<string> {
  const raw = sessionStorage.getItem(EXPANDED_KEY) ?? '';
  if (raw === cachedRaw) {
    return cachedSnapshot;
  }
  cachedRaw = raw;
  try {
    cachedSnapshot = new Set(JSON.parse(raw) as string[]);
  } catch {
    cachedSnapshot = new Set();
  }
  return cachedSnapshot;
}

function getServerSnapshot (): ReadonlySet<string> {
  return new Set();
}

function writeExpandedIds (ids: Iterable<string>): void {
  sessionStorage.setItem(EXPANDED_KEY, JSON.stringify([ ...ids ]));
  dispatch();
}

function readGroupIds (): string[] {
  try {
    const raw = sessionStorage.getItem(GROUP_IDS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
interface PayoutExpandToolbarState {
  hasExpandedAny: boolean;
  toggleAll: () => void;
}

export function usePayoutExpandToolbar (): PayoutExpandToolbarState {
  const expandedIds = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  function toggleAll (): void {
    const allIds = readGroupIds();
    const allExpanded = allIds.length > 0 && allIds.every((id) => expandedIds.has(id));
    writeExpandedIds(allExpanded ? [] : allIds);
  }

  return { hasExpandedAny: expandedIds.size > 0, toggleAll };
}

interface GrandTotalTableRow {
  kind: 'grand-total';
  id: string;
  cells: (string | null)[];
}

interface GroupTableRow {
  kind: 'group';
  id: string;
  node: PayoutGroup;
}

interface LeafTableRow {
  kind: 'leaf';
  id: string;
  node: PayoutLeaf;
}

type PayoutTableRow = GrandTotalTableRow | GroupTableRow | LeafTableRow;

interface PayoutTableProps {
  data: PayoutContentData | undefined;
  organizationName: string;
}

export function PayoutTable ({ data, organizationName }: PayoutTableProps): ReactElement {
  const expandedIds = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  function toggleExpanded (id: string): void {
    const next = new Set(expandedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    writeExpandedIds(next);
  }

  // Register the current tree's group IDs so the toolbar toggleAll can
  // expand/collapse without direct access to the data.
  const tree = data?.tree ?? [];
  const allGroupIds = getAllGroupIds(tree);
  const storedGroupIds = readGroupIds();
  if (JSON.stringify(allGroupIds) !== JSON.stringify(storedGroupIds)) {
    sessionStorage.setItem(GROUP_IDS_KEY, JSON.stringify(allGroupIds));
  }

  const columns = data?.columns ?? [];
  const grandTotal = data?.grandTotal ?? null;

  const rows: PayoutTableRow[] = [
    ...(grandTotal ? [{ kind: 'grand-total' as const, id: 'grand-total', cells: grandTotal }] : []),
    ...flattenTree(tree, expandedIds),
  ];

  if (!data) {
    return (
      <EmptyState style={emptyStateStyles.base}>
        <Heading>No payout data for {organizationName}</Heading>
      </EmptyState>
    );
  }

  return (
    <View style={pageStyles.scrollArea}>
      <div {...stylex.props(tableContainerStyles.base)}>
        <Table
          aria-label="Payout report"
          selectionMode="none"
          style={tableStyles.base}>
          <TableHeader style={tableHeaderStyles.sticky}>
            {columns.map((col, i) => (
              <Column
                defaultWidth={COLUMN_WIDTHS[col.id] ?? 100}
                id={col.id}
                isRowHeader={i === 0}
                key={col.id}
                minWidth={COLUMN_WIDTHS[col.id] ?? 64}>
                <Text>{col.name}</Text>
              </Column>
            ))}
          </TableHeader>

          <TableBody<PayoutTableRow> items={rows}>
            {(row) => {
              if (row.kind === 'grand-total') {
                return (
                  <Row id={row.id}>
                    {columns.map((col, colIdx) => (
                      <Cell
                        align={colIdx === 0 ? 'left' : 'right'}
                        key={col.id}
                        textValue={colIdx === 0 ? 'Grand Total' : (row.cells[colIdx] ?? '')}>
                        {colIdx === 0 ? (
                          <Text color="primary" weight="medium">Grand Total</Text>
                        ) : (
                          <Text color="primary" weight="medium">
                            {row.cells[colIdx] ?? ''}
                          </Text>
                        )}
                      </Cell>
                    ))}
                  </Row>
                );
              }

              if (row.kind === 'group') {
                const group = row.node;
                const isExpanded = expandedIds.has(group.id);
                const hasChildren = group.children.length > 0;

                return (
                  <Row
                    id={row.id}
                    style={hasChildren ? groupRowStyles.clickable : undefined}
                    onAction={hasChildren ? () => toggleExpanded(group.id) : undefined}>
                    {columns.map((col, colIdx) => (
                      <Cell
                        align={colIdx === 0 ? 'left' : 'right'}
                        key={col.id}
                        textValue={colIdx === 0 ? group.name : (group.cells[colIdx] ?? '')}>
                        {colIdx === 0 ? (
                          <div {...stylex.props(nameCellStyles.base, group.depth > 0 && nameCellStyles.indented)}>
                            {hasChildren ? (
                              <span {...stylex.props(chevronStyles.icon)}>
                                {isExpanded
                                  ? <ChevronDownStrokeIcon color="tertiary" size={12} slot={null!} />
                                  : <ChevronRightStrokeIcon color="tertiary" size={12} slot={null!} />}
                              </span>
                            ) : (
                              <span {...stylex.props(chevronStyles.spacer)} />
                            )}
                            <Text color="primary" weight="medium">
                              {group.name}
                            </Text>
                          </div>
                        ) : (
                          <Text color="primary" weight="medium">
                            {group.cells[colIdx] ?? ''}
                          </Text>
                        )}
                      </Cell>
                    ))}
                  </Row>
                );
              }

              const leaf = row.node;
              return (
                <Row id={row.id}>
                  {columns.map((col, colIdx) => (
                    <Cell
                      align={colIdx > 1 ? 'right' : 'left'}
                      key={col.id}
                      textValue={colIdx === 0 ? '' : colIdx === 1 ? leaf.date : (leaf.cells[colIdx] ?? '')}>
                      {colIdx === 0 ? null : colIdx === 1 ? (
                        <Text color="secondary">{leaf.date}</Text>
                      ) : (
                        <Text color="secondary">
                          {leaf.cells[colIdx] ?? ''}
                        </Text>
                      )}
                    </Cell>
                  ))}
                </Row>
              );
            }}
          </TableBody>
        </Table>
      </div>
    </View>
  );
}

function getAllGroupIds (tree: PayoutGroup[]): string[] {
  const ids: string[] = [];
  function visit (node: PayoutTreeNode): void {
    if (node.kind === 'group') {
      ids.push(node.id);
      for (const child of node.children) {
        visit(child);
      }
    }
  }
  for (const root of tree) {
    visit(root);
  }
  return ids;
}

function flattenTree (tree: PayoutGroup[], expandedIds: ReadonlySet<string>): PayoutTableRow[] {
  const rows: PayoutTableRow[] = [];

  function visit (node: PayoutTreeNode): void {
    if (node.kind === 'group') {
      rows.push({ kind: 'group', id: node.id, node });
      if (expandedIds.has(node.id)) {
        for (const child of node.children) {
          visit(child);
        }
      }
    } else {
      rows.push({ kind: 'leaf', id: node.id, node });
    }
  }

  for (const root of tree) {
    visit(root);
  }

  return rows;
}

const COLUMN_WIDTHS: Record<string, number> = {
  'col-website': 140,
  'col-date': 96,
  'col-usd': 102,
  'col-credits': 108,
  'col-avg-cost': 137,
  'col-pageviews': 88,
  'col-rpm': 64,
  'col-outbound-clicks': 117,
  'col-ctr': 64,
};

const pageStyles = stylex.create({
  scrollArea: {
    height: '100%',
    position: 'relative',
    width: '100%',
  },
});

const tableContainerStyles = stylex.create({
  base: {
    height: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
  },
});

const tableHeaderStyles = stylex.create({
  sticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
});

const tableStyles = stylex.create({
  base: {
    borderSpacing: 0,
    minWidth: '100%',
    width: 'max-content',
  },
});

const nameCellStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    overflow: 'hidden',
    width: '100%',
  },
  indented: {
    paddingLeft: 32,
  },
});

const groupRowStyles = stylex.create({
  clickable: {
    cursor: 'pointer',
  },
});

const chevronStyles = stylex.create({
  icon: {
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    width: 20,
  },
  spacer: {
    display: 'inline-block',
    flexShrink: 0,
    height: 20,
    width: 20,
  },
});

const emptyStateStyles = stylex.create({
  base: {
    paddingTop: 90,
  },
});
